import { useCallback, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactTable from 'react-table';
import produce from 'immer';
import { makeStyles } from '@material-ui/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import { Close } from '@material-ui/icons';
import { toast } from 'react-toastify';
import CardBody from 'components/Card/CardBody';
import CardWrapper from 'components/Card/CardWrapper';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import FormControlStickyButton from 'components/FormControlStickyButton/FormControlStickyButton';
import { get, put } from 'helpers/apiHelpers';
import { isGranted } from 'helpers/helpers';
import roles from 'helpers/roles';

import columnConfig from './columnConfig';

const useStyles = makeStyles({
  priceInput: {
    fontSize: 14,
    paddingLeft: 0,
    paddingTop: 5,
    paddingBottom: 5,
  },
  description: {
    margin: '0 0 16px',
    color: '#3c4858',
  },
  modifiedRow: {
    backgroundColor: '#ffffd4 !important',
  },
  dialogPaper: {
    borderRadius: 12,
    padding: '4px 16px 20px',
    minWidth: 640,
    textAlign: 'center',
  },
  closeRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  dialogTitle: {
    paddingTop: 0,
    paddingBottom: 24,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: 600,
  },
  dialogContent: {
    textAlign: 'center',
    color: '#5f6368',
    fontSize: 14,
    fontWeight: 500,
    paddingBottom: 24,
  },
  dialogActions: {
    justifyContent: 'center',
  },
  dialogButton: {
    margin: '0 5px',
  },
});

const MealTypeSizePrices = ({ priceField, descriptionKey }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { currency } = useSelector(state => state.Brands.brand.multinational);
  const canEdit = isGranted(roles.ROLE_EDIT_MEAL_TYPE);

  const [rows, setRows] = useState([]);
  const [pendingLocation, setPendingLocation] = useState(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  useEffect(() => {
    const fetchRows = async () => {
      const response = await get('/meal-type-sizes', { pagination: false });
      const members = response?.['hydra:member'] ?? [];
      setRows(
        members.map(item => ({
          '@id': item['@id'],
          mealTypeName: item?.mealType?.name || '',
          calories: item?.calories ?? null,
          value: item[priceField] ?? '',
          savedValue: item[priceField] ?? '',
          hasChanged: false,
        }))
      );
    };

    fetchRows();
  }, [priceField]);

  const isDirty = useMemo(() => rows.some(row => row.hasChanged), [rows]);

  const handleValueChange = useCallback((iri, rawValue) => {
    const newValue = rawValue.replace(/,/g, '.');
    if (!/^[0-9.]*$/.test(newValue)) {
      return;
    }
    setRows(
      produce(draft => {
        const row = draft.find(item => item['@id'] === iri);
        if (!row) {
          return;
        }
        row.value = newValue;
        row.hasChanged = parseFloat(row.value) !== parseFloat(row.savedValue);
      })
    );
  }, []);

  const saveAll = async () => {
    const changed = rows.filter(row => row.hasChanged);
    if (changed.length === 0) {
      return;
    }
    try {
      await Promise.all(
        changed.map(row =>
          put(row['@id'], { [priceField]: parseFloat(row.value) })
        )
      );
      setRows(
        produce(draft => {
          draft.forEach(row => {
            row.savedValue = row.value;
            row.hasChanged = false;
          });
        })
      );
      toast.success(t('success.changesSaved'));
    } catch (error) {
      toast.error(
        error?.response?.data?.['hydra:description'] ??
          t('common.shared.saveError')
      );
      throw error;
    }
  };

  const undoAll = () => {
    setRows(
      produce(draft => {
        draft.forEach(row => {
          row.value = row.savedValue;
          row.hasChanged = false;
        });
      })
    );
    toast.success(t('$*priceLists.mealTypeSizes.changesReverted'));
  };

  const handleBlockedNavigation = nextLocation => {
    if (confirmedNavigation) {
      return true;
    }
    setPendingLocation(nextLocation);
    return false;
  };

  useEffect(() => {
    if (confirmedNavigation && pendingLocation) {
      history.push(`${pendingLocation.pathname}${pendingLocation.search}`);
    }
  }, [confirmedNavigation, pendingLocation, history]);

  const closeLeaveDialog = () => setPendingLocation(null);

  const handleSaveAndLeave = async () => {
    try {
      await saveAll();
      setConfirmedNavigation(true);
    } catch {
      setPendingLocation(null);
    }
  };

  const handleLeaveWithoutSaving = () => setConfirmedNavigation(true);

  const columns = useMemo(
    () =>
      columnConfig({
        t,
        currency,
        canEdit,
        priceInputClassName: classes.priceInput,
        onValueChange: handleValueChange,
      }),
    [t, canEdit, currency, classes.priceInput, handleValueChange]
  );

  return (
    <>
      <Prompt when={isDirty} message={handleBlockedNavigation} />

      <CardWrapper title={t('$*shop.defaultPriceMeals.defaultMealsPrices')}>
        <CardBody>
          <p className={classes.description}>{t(descriptionKey)}</p>
          <GridContainer>
            <GridItem xs={12}>
              <ReactTable
                data={rows}
                resizable={false}
                filterable={true}
                columns={columns}
                minRows={5}
                showPaginationTop={true}
                showPaginationBottom={true}
                className="-striped -highlight"
                getTrProps={(state, rowInfo) =>
                  rowInfo?.original?.hasChanged
                    ? { className: classes.modifiedRow }
                    : {}
                }
                previousText={t('dataGrid.prevPage')}
                nextText={t('dataGrid.nextPage')}
                loadingText={t('dataGrid.loading')}
                noDataText={t('dataGrid.notFound')}
                pageText={t('dataGrid.page')}
                ofText={t('dataGrid.pageOf')}
                rowsText={t('dataGrid.results')}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      {canEdit && (
        <FormControlStickyButton
          submitText={t('common.shared.save')}
          handleSubmit={saveAll}
          submitDisabled={!isDirty}
          customButtonsLeft={[
            {
              label: t('$*priceLists.mealTypeSizes.revertChanges'),
              onClick: undoAll,
              disabled: !isDirty,
            },
          ]}
        />
      )}

      <Dialog
        maxWidth="xs"
        open={Boolean(pendingLocation)}
        onClose={closeLeaveDialog}
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.closeRow}>
          <IconButton onClick={closeLeaveDialog} size="small">
            <Close />
          </IconButton>
        </div>
        <DialogTitle disableTypography={true} className={classes.dialogTitle}>
          {t('$*priceLists.mealTypeSizes.leaveTitle')}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {t('$*priceLists.mealTypeSizes.leaveSubtitle')}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            color="danger"
            className={classes.dialogButton}
            onClick={handleLeaveWithoutSaving}
          >
            {t('$*priceLists.mealTypeSizes.leaveDontSave')}
          </Button>
          <Button
            color="success"
            className={classes.dialogButton}
            onClick={handleSaveAndLeave}
          >
            {t('$*priceLists.mealTypeSizes.leaveAndSave')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MealTypeSizePrices;
